import * as React from 'react'
import { Checkbox } from './checkbox'
import { cn } from 'app/lib/utils'

interface CheckboxGroupItemProps {
  value: string
  children: React.ReactNode
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
}

interface OptionItem {
  label: string
  value: string
  disabled?: boolean
}

interface CheckboxGroupProps {
  /**
   * The initial value of the checkboxGroup.
   */
  value?: string[]
  onValueChange?: (value: string[]) => void
  options?: OptionItem[]
  defaultValue?: string[]
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
  /**
   * 表单组件名称，用于表单提交时获取数据
   */
  name?: string
  searchPlaceholder?: string
  noFound?: React.ReactNode
}

function CheckboxItem({ value, children, onCheckedChange }: CheckboxGroupItemProps) {
  const [fieldChecked, setFieldChecked] = useCheckboxField(value)
  return (
    <Checkbox
      checked={fieldChecked}
      value={value}
      onCheckedChange={(checked) => {
        setFieldChecked(checked as boolean)
        onCheckedChange?.(checked as boolean)
      }}
    >
      {children}
    </Checkbox>
  )
}

function CheckboxGroup({ options = [], defaultValue, value, onValueChange, children, className, style }: CheckboxGroupProps) {
  const checkedValues = value ?? defaultValue ?? []

  return (
    <CheckboxContext.Provider
      value={{
        checkboxData: value,
        setCheckboxFieldData: (value: string, checked: boolean) => {
          if (checked) {
            onValueChange?.([...checkedValues, value])
          } else {
            onValueChange?.(checkedValues?.filter?.((item) => item !== value) || [])
          }
        },
      }}
    >
      <div style={style} className={cn('flex flex-wrap items-center space-x-4', className)}>
        {options.map((option) => (
          <div className="flex items-center space-x-2" key={option.value}>
            <CheckboxItem value={option.value}>{option.label}</CheckboxItem>
          </div>
        ))}
        {children}
      </div>
    </CheckboxContext.Provider>
  )
}
CheckboxGroup.Item = CheckboxItem

interface CheckboxContext {
  checkboxData?: string[]
  setCheckboxFieldData: (value: string, checked: boolean) => void
}

const CheckboxContext = React.createContext<CheckboxContext>({
  checkboxData: [],
  setCheckboxFieldData: () => {},
})

const useCheckboxContext = () => React.useContext(CheckboxContext)

const useCheckboxField = (value?: string) => {
  const { checkboxData, setCheckboxFieldData } = useCheckboxContext()
  if (value === undefined) return [undefined, () => {}] as const
  return [checkboxData?.includes(value), (checked: boolean) => setCheckboxFieldData(value, checked)] as const
}

export { CheckboxGroup, CheckboxItem }
